import React, { useState, useEffect, useCallback } from 'react';
import { useCall } from '../../context/CallContext';

// ─────────────────────────────────────────────────────────────────────────
// GlobalCallOverlay — PURE UI, no WebRTC logic of its own.
// All call state/media comes from useCall() (the single shared CallContext
// instance).
//
// CRITICAL FIX (this revision): the local/remote <video> elements are now
// rendered EXACTLY ONCE, outside the per-phase (calling/ringing/active)
// conditional blocks, and simply hidden/shown/resized with CSS. Previously
// each call phase (calling vs active, video vs audio) had its OWN <video>
// JSX block — so when callState went calling → active, React unmounted the
// "calling" phase's <video> elements and mounted brand new ones for the
// "active" phase. The MediaStream object survived (it's stored in a ref in
// useCallRTC), but the freshly mounted DOM elements had no srcObject until
// something explicitly re-attached it — and for the LOCAL stream nothing
// did that automatically. This is exactly why the caller side had no
// outgoing audio/video and disappearing mute/camera controls: the controls
// only existed in the "active" JSX block, which didn't exist yet during
// "calling", and once "active" mounted, the local mic stream wasn't
// re-attached to the new element.
// ─────────────────────────────────────────────────────────────────────────

export default function GlobalCallOverlay() {
  const call = useCall();
  const {
    callState, callType, incomingOffer, callerName,
    isMuted, isCamOff,
    localVideoRef, remoteVideoRef,
    answerCall, rejectCall, endCall,
    toggleMute, toggleCam,
    attachPendingRemoteStream,
  } = call;

  const [duration, setDuration] = useState(0);
  const [swapped,  setSwapped]  = useState(false);

  useEffect(() => {
    if (callState !== 'active') { setDuration(0); return; }
    const t = setInterval(() => setDuration(d => d + 1), 1000);
    return () => clearInterval(t);
  }, [callState]);

  useEffect(() => { if (callState === 'idle') setSwapped(false); }, [callState]);

  // Stable ref callbacks — these are the ONLY place refs get assigned,
  // and since the <video> elements are now always-mounted (see below),
  // these callbacks fire ONCE per call, not once per phase transition.
  const remoteRefCb = useCallback((el) => {
    remoteVideoRef.current = el;
    if (el) {
      el.muted = false; el.volume = 1;
      if (el.srcObject) el.play?.().catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const localRefCb = useCallback((el) => {
    localVideoRef.current = el;
    if (el && el.srcObject) el.play?.().catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever we transition into 'active' (audio connects, or video stream
  // arrives), make sure whatever stream is already held in the hook's refs
  // gets (re)applied to these persistent elements. Cheap/no-op if already
  // attached, but guarantees we never end up with empty srcObject.
  useEffect(() => {
    if (callState === 'calling' || callState === 'active') {
      attachPendingRemoteStream?.();
    }
  }, [callState, attachPendingRemoteStream]);

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  if (callState === 'idle') return null;

  const isVideo  = callType === 'video';
  const isCalling = callState === 'calling';
  const isRinging = callState === 'ringing';
  const isActive  = callState === 'active';

  // ── Ringing (incoming) — separate full modal, no persistent video needed
  //    yet since media hasn't been requested for an incoming call until
  //    the user taps Answer.
  if (isRinging && incomingOffer) {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          background: '#1a1a2e', borderRadius: 28, padding: '36px 28px',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 18, width: 300, boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}>
          <div style={{ position: 'relative', width: 90, height: 90 }}>
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              background: isVideo ? '#3B82F6' : '#10B981',
              animation: 'gcoPulse 1.5s ease-out infinite',
            }} />
            <div style={{
              position: 'relative', width: 90, height: 90, borderRadius: '50%',
              background: isVideo ? '#2563EB' : '#059669',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1, fontSize: 36,
            }}>
              {isVideo ? '📹' : '📞'}
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#fff', fontSize: 19, fontWeight: 800, margin: 0 }}>
              {callerName || 'Unknown'}
            </p>
            <p style={{ color: '#94A3B8', fontSize: 13, margin: '6px 0 0' }}>
              Incoming {isVideo ? 'Video' : 'Audio'} Call
            </p>
          </div>

          <div style={{ display: 'flex', gap: 16, width: '100%' }}>
            <button onClick={rejectCall} style={{
              flex: 1, padding: '14px 0', borderRadius: 50,
              background: '#EF4444', border: 'none', color: '#fff',
              fontSize: 15, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              <span>✕</span> Decline
            </button>
            <button onClick={answerCall} style={{
              flex: 1, padding: '14px 0', borderRadius: 50,
              background: isVideo ? '#2563EB' : '#10B981', border: 'none', color: '#fff',
              fontSize: 15, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              <span>✓</span> Answer
            </button>
          </div>
        </div>

        <style>{`
          @keyframes gcoPulse {
            0%   { transform: scale(1);   opacity: 0.7; }
            70%  { transform: scale(1.5); opacity: 0; }
            100% { transform: scale(1.5); opacity: 0; }
          }
        `}</style>
      </div>
    );
  }

  // ── Calling + Active — SINGLE unified layout. The phase (calling vs
  //    active) only changes overlay text/controls visibility, never the
  //    <video> elements themselves, so the mic/camera stream stays
  //    attached continuously from the moment getUserMedia() resolves.
  if (isCalling || isActive) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: isVideo ? '#0a0a0a' : 'linear-gradient(135deg,#0D9488,#0F766E)',
        display: 'flex', flexDirection: 'column',
        userSelect: 'none', WebkitUserSelect: 'none',
      }}>

        {isVideo ? (
          <div style={{ position: 'relative', flex: 1, overflow: 'hidden', background: '#0a0a0a' }}>

            {/* Remote video — full screen by default, shrinks to PIP when swapped.
                Always mounted: only CSS position/size changes. */}
            <video
              ref={remoteRefCb}
              autoPlay playsInline
              style={{
                position: 'absolute',
                top: swapped ? 'auto' : 0,
                left: swapped ? 'auto' : 0,
                bottom: swapped ? 16 : 'auto',
                right: swapped ? 16 : 'auto',
                width:  swapped ? 90  : '100%',
                height: swapped ? 120 : '100%',
                objectFit: 'cover',
                background: '#111',
                borderRadius: swapped ? 14 : 0,
                border: swapped ? '2.5px solid rgba(255,255,255,0.85)' : 'none',
                boxShadow: swapped ? '0 4px 20px rgba(0,0,0,0.5)' : 'none',
                zIndex: swapped ? 2 : 1,
                cursor: swapped ? 'pointer' : 'default',
                opacity: isActive ? 1 : 0.3,   // dim until the other side actually connects
                transition: 'width 0.25s ease, height 0.25s ease, border-radius 0.25s ease, opacity 0.3s ease',
              }}
              onClick={() => swapped && setSwapped(false)}
            />

            {/* Local video — PIP by default, swaps to full screen */}
            <video
              ref={localRefCb}
              autoPlay playsInline
              muted
              style={{
                position: 'absolute',
                top: swapped ? 0 : 'auto',
                left: swapped ? 0 : 'auto',
                bottom: swapped ? 'auto' : 16,
                right: swapped ? 'auto' : 16,
                width:  swapped ? '100%' : 90,
                height: swapped ? '100%' : 120,
                objectFit: 'cover',
                background: '#222',
                borderRadius: swapped ? 0 : 14,
                border: swapped ? 'none' : '2.5px solid rgba(255,255,255,0.85)',
                boxShadow: swapped ? 'none' : '0 4px 20px rgba(0,0,0,0.5)',
                zIndex: swapped ? 1 : 2,
                cursor: swapped ? 'default' : 'pointer',
                transition: 'width 0.25s ease, height 0.25s ease, border-radius 0.25s ease',
              }}
              onClick={() => !swapped && setSwapped(true)}
            />

            <div onClick={() => setSwapped(s => !s)} style={{
              position: 'absolute',
              bottom: swapped ? 'auto' : 20,
              right:  swapped ? 'auto' : 20,
              top:    swapped ? 16 : 'auto',
              left:   swapped ? 16 : 'auto',
              width: 26, height: 26,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 3, cursor: 'pointer',
            }}>
              <span style={{ fontSize: 13 }}>⇄</span>
            </div>

            {isCamOff && (
              <div style={{
                position: 'absolute',
                ...(swapped
                  ? { top:0, left:0, width:'100%', height:'100%' }
                  : { bottom:16, right:16, width:90, height:120, borderRadius:14 }
                ),
                background: '#1a1a1a',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 4,
              }}>
                <span style={{ fontSize: swapped ? 40 : 18 }}>📵</span>
              </div>
            )}

            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0,
              padding: '20px 20px 40px',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.65), transparent)',
              zIndex: 5, pointerEvents: 'none',
            }}>
              <p style={{ color: '#fff', fontWeight: 700, fontSize: 16, margin: 0 }}>{callerName || 'Unknown'}</p>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, margin: '4px 0 0' }}>
                {isActive ? fmt(duration) : 'Calling...'}
              </p>
            </div>
          </div>

        ) : (
          /* ── Audio call — avatar layout. Video elements still mounted
                (hidden, 1×1px) so the mic stream attaches the instant
                getUserMedia resolves and never needs to be re-attached
                when calling → active happens. */
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
            <div style={{
              width: 110, height: 110, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)', border: '4px solid rgba(255,255,255,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48,
            }}>👤</div>
            <p style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: 0 }}>{callerName || 'Unknown'}</p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, margin: 0 }}>
              {isActive ? fmt(duration) : 'Calling...'}
            </p>
            <video
              ref={remoteRefCb}
              autoPlay playsInline
              style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
            />
            <video ref={localRefCb} autoPlay playsInline muted
              style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
          </div>
        )}

        {/* ── Controls bar — ALWAYS visible during calling AND active, so
              mute/camera are available from the moment local media starts,
              not just after the other side answers. */}
        <div style={{
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(14px)',
          padding: '20px 0 30px', display: 'flex', justifyContent: 'center',
          gap: 28, flexShrink: 0,
        }}>
          <button onClick={toggleMute} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: isMuted ? '#EF4444' : 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontSize: 22 }}>{isMuted ? '🔇' : '🎙️'}</span>
            </div>
            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>{isMuted ? 'Unmute' : 'Mute'}</span>
          </button>

          {isVideo && (
            <button onClick={toggleCam} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: isCamOff ? '#EF4444' : 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: 22 }}>{isCamOff ? '📵' : '📷'}</span>
              </div>
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>Camera</span>
            </button>
          )}

          <button onClick={endCall} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: '#EF4444', boxShadow: '0 4px 16px rgba(239,68,68,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontSize: 22 }}>📵</span>
            </div>
            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>End</span>
          </button>
        </div>
      </div>
    );
  }

  return null;
}
